/* eslint-disable @typescript-eslint/no-explicit-any */
import { Object as FabricObject, Group, Textbox } from 'fabric';

import { bubble } from '@app/app/lib';

import { useCanvasStore } from '../../store';

import { MenuItem, type AlignmentText } from '../../types/types';

import { CANVAS_EVENTS, SELECTORS } from '../../config/constants';

import { isBullet, performActionOnObjectInGroup } from '../groupUtils';
import { TextEntity } from '../../metaBullets/classes/TextEntity';

type MenuItemWithBackup = MenuItem & {
  addBackup: (canvasId: string, data: any) => void;
};

/**
 * Изменяет цвет текста или выделенной части текста
 * @param params - Параметры для изменения цвета
 */
export const colorizeText = ({
  color,
  canvas,
  targetIds,
  canvasId,
  addBackup,
}: MenuItemWithBackup & { color: string }) => {
  const canvasObjects = canvas.getObjects();
  targetIds.forEach((id) => {
    const obj = canvasObjects.find((o: FabricObject) => {
      if (o.id === id) {
        return true;
      } else if (o.category) {
        //if the object to be deleted is in a group (bullets)
        if (isBullet(o) && o instanceof Group) {
          performActionOnObjectInGroup(o, id, (obj) => {
            if (
              (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity') &&
              (obj instanceof Textbox || obj instanceof TextEntity)
            ) {
              const textObject = obj as Textbox | TextEntity;
              // When user modifies text color, we assume they want to keep this version of text
              // and don't need to restore the original text anymore
              textObject.set({
                fill: color,
                is_truncated: false,
              });
              //iterate through all styles and change only the necessary ones
              Object.keys(textObject.styles).forEach((key) => {
                const s = textObject.styles[key];
                Object.values(s).forEach((style: any) => {
                  if (style.fill) {
                    delete style.fill; //remove fill
                  }
                });
              });
              textObject.dirty = true;
            }
          });
          return; //object found and processed
        }
      }
    });
    if (obj instanceof Textbox || obj instanceof TextEntity) {
      if (
        obj &&
        (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity')
      ) {
        const textObject = obj as Textbox | TextEntity;
        try {
          // When user modifies text color, we assume they want to keep this version of text
          // and don't need to restore the original text anymore
          textObject.set('is_truncated', false);

          if (textObject.selectionStart === textObject.selectionEnd) {
            // No selection -> colorize the whole text
            textObject.set('fill', color);
            //iterate through all styles and change only the necessary ones
            Object.keys(textObject.styles).forEach((key) => {
              const s = textObject.styles[key];
              Object.values(s).forEach((style: any) => {
                if (style.fill) {
                  delete style.fill; //remove fill
                }
              });
            });
            textObject.dirty = true;
          } else {
            // There is selection -> colorize only the selected part
            textObject.setSelectionStyles(
              { fill: color },
              textObject.selectionStart,
              textObject.selectionEnd,
            );
            textObject.dirty = true;
          }
        } catch (e) {
          console.error('⛔ Error change color', e);
        }
      }
    }
    const cfg = canvas.toJSON();
    addBackup(canvasId, cfg);
    canvas.requestRenderAll();
  });
};

/**
 * Изменяет выравнивание текста
 * @param params - Параметры для изменения выравнивания
 */
export const alignText = ({
  targetIds,
  canvas,
  canvasId,
  pos,
}: MenuItem & { pos: AlignmentText }) => {
  const { addBackup } = useCanvasStore.getState();
  const idsToAction = Array.isArray(targetIds) ? targetIds : [targetIds];
  idsToAction.forEach((currentId) => {
    const obj = canvas.getObjects().find((o) => {
      if (o.id === currentId) {
        return true;
      } else if (o.category) {
        //if the object to be deleted is in a group (bullets)
        if (isBullet(o) && o instanceof Group) {
          performActionOnObjectInGroup(o, currentId, (obj) => {
            // When user modifies text alignment, we assume they want to keep this version of text
            // and don't need to restore the original text anymore
            obj.set({
              textAlign: pos,
              is_truncated: false,
            });
          });
          return; //object found and processed
        }
      }
    });
    if (obj) {
      // When user modifies text alignment, we assume they want to keep this version of text
      // and don't need to restore the original text anymore
      obj.set({
        textAlign: pos,
        is_truncated: false,
      });
    }
  });
  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Изменяет размер шрифта текста
 * @param params - Параметры для изменения размера шрифта
 * @param adjustment - Изменение размера (положительное для увеличения, отрицательное для уменьшения)
 * @param isNeedUpdateContextMenu - Нужно ли обновлять контекстное меню
 */
export const adjustFontSize = (
  { targetIds, canvas, canvasId, addBackup }: MenuItemWithBackup,
  adjustment: number,
  isNeedUpdateContextMenu = false,
) => {
  try {
    const canvasEl = document.querySelector(SELECTORS.canvas(canvasId));
    let isTextInBulletDetected = false; // Flag to track presence of text in bullets

    const bubbleFromMenu = (detail: any = {}) => {
      if (isNeedUpdateContextMenu) {
        //is need because of the context menu is not updated automatically (not working event canvas.on('object:modified'))
        if (canvasEl) {
          bubble(canvasEl as HTMLElement, CANVAS_EVENTS.UPDATE_CONTEXT_MENU, detail);
        }
      }
    };

    // Function to preserve the position of the top left corner when changing the font size
    const adjustFontSizeWithPositionPreservation = (textObject: Textbox | TextEntity) => {
      // Current font size
      const currentFontSize = textObject.fontSize || 16;
      // New font size
      const newFontSize = Math.max(1, currentFontSize + adjustment);

      // Apply new font size
      textObject.set({
        fontSize: newFontSize,
        is_truncated: false,
      });

      // Очищаем кэш и обновляем координаты, но НЕ изменяем размеры коробки
      textObject._clearCache();
      textObject.setCoords();
      // УБИРАЕМ автоматический пересчет размеров - пользователь сам решает размер коробки
      // recalculateTextboxDimensions(textObject, canvas);

      // ✅ Уведомляем MetaBullet об изменении размера (для пересчета позиций)
      if (TextEntity.isTextEntity(textObject)) {
        const textEntity = textObject as TextEntity;
        (textEntity as any).notifyMetaBulletChanged?.('size_changed', {
          newWidth: textEntity.width,
          newHeight: textEntity.height,
          fontSize: newFontSize,
        });
      }
    };

    const objects = canvas.getObjects();
    targetIds.forEach((id) => {
      const targetObject = objects.find((obj) => {
        if (obj.id === id) {
          return true;
        } else if (obj.category) {
          //if the object to be deleted is in a group (bullets)
          if (isBullet(obj) && obj instanceof Group) {
            performActionOnObjectInGroup(obj, id, (innerObj) => {
              if (
                innerObj.type.toLowerCase() === 'textbox' ||
                innerObj.type.toLowerCase() === 'textentity'
              ) {
                const textObject = innerObj as Textbox | TextEntity;

                // Применяем функцию изменения размера с сохранением позиции
                adjustFontSizeWithPositionPreservation(textObject);
              }
            });
            isTextInBulletDetected = true; // Mark that we found text in bullet
            return; //object found and processed
          }
        }
      });

      if (
        targetObject &&
        (targetObject.type.toLowerCase() === 'textbox' ||
          targetObject.type.toLowerCase() === 'textentity')
      ) {
        const textObject = targetObject as Textbox | TextEntity;

        // Apply function to change size with position preservation
        adjustFontSizeWithPositionPreservation(textObject);
      }
    });

    const cfg = canvas.toJSON();
    addBackup(canvasId, cfg);
    canvas.requestRenderAll();

    // Call the event of updating the context menu only once at the end
    // with the correct parameters depending on the type of object
    if (isTextInBulletDetected) {
      bubbleFromMenu({ isTextInBullet: true });
    } else {
      bubbleFromMenu();
    }
  } catch (error) {
    console.error('Error adjusting font size:', error);
  }
};

/**
 * Применяет стиль форматирования к тексту (жирный, обычный, подчеркнутый)
 * @param params - Параметры для применения стиля
 */
export const applyFontStyle = ({
  style,
  canvas,
  targetIds,
  canvasId,
  addBackup,
}: MenuItemWithBackup & { style: 'bold' | 'regular' | 'underline' }) => {
  const canvasObjects = canvas.getObjects();
  targetIds.forEach((id) => {
    const obj = canvasObjects.find((o: FabricObject) => {
      if (o.id === id) {
        return true;
      } else if (o.category) {
        //if the object to be deleted is in a group (bullets)
        if (isBullet(o) && o instanceof Group) {
          performActionOnObjectInGroup(o, id, (obj) => {
            if (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity') {
              const textObject = obj as Textbox | TextEntity;

              // When user modifies font style, we assume they want to keep this version of text
              // and don't need to restore the original text anymore
              textObject.set('is_truncated', false);

              switch (style) {
                case 'regular':
                  textObject.set({
                    fontWeight: 'normal',
                    fontStyle: 'normal',
                    underline: false,
                  });
                  //processing textObject.styles (standard Fabric.js format)
                  Object.keys(textObject.styles).forEach((key) => {
                    const s = textObject.styles[key];
                    Object.values(s).forEach((style: any) => {
                      if (style.fontWeight) delete style.fontWeight;
                      if (style.underline) delete style.underline;
                    });
                  });

                  //processing array styles with fields start, end, style
                  if (Array.isArray(textObject.styles)) {
                    textObject.styles.forEach((styleItem: any) => {
                      if (styleItem.style) {
                        if (styleItem.style.fontWeight) delete styleItem.style.fontWeight;
                        if (styleItem.style.underline) delete styleItem.style.underline;
                      }
                    });
                  }
                  textObject.dirty = true;
                  textObject._clearCache();
                  textObject.setCoords();
                  break;
                case 'bold':
                  textObject.set({
                    fontWeight: 'bold',
                    fontStyle: 'normal',
                    underline: false,
                  });
                  //processing textObject.styles (standard Fabric.js format)
                  Object.keys(textObject.styles).forEach((key) => {
                    const s = textObject.styles[key];
                    Object.values(s).forEach((style: any) => {
                      style.fontWeight = 'bold';
                      //save other styles if they exist
                    });
                  });

                  //processing array styles with fields start, end, style
                  if (Array.isArray(textObject.styles)) {
                    textObject.styles.forEach((styleItem: any) => {
                      if (styleItem.style) {
                        styleItem.style.fontWeight = 'bold';
                      }
                    });
                  }
                  textObject.dirty = true;
                  textObject._clearCache();
                  textObject.setCoords();
                  break;
                case 'underline':
                  textObject.set({
                    fontWeight: 'normal',
                    fontStyle: 'normal',
                    underline: true,
                  });
                  //processing textObject.styles (standard Fabric.js format)
                  Object.keys(textObject.styles).forEach((key) => {
                    const s = textObject.styles[key];
                    Object.values(s).forEach((style: any) => {
                      style.underline = true;
                      //save other styles if they exist
                    });
                  });

                  //processing array styles with fields start, end, style
                  if (Array.isArray(textObject.styles)) {
                    textObject.styles.forEach((styleItem: any) => {
                      if (styleItem.style) {
                        styleItem.style.underline = true;
                      }
                    });
                  }
                  textObject.dirty = true;
                  textObject._clearCache();
                  textObject.setCoords();
                  break;
              }
            }
          });
          return; //object found and processed
        }
      }
    });
    if (obj && (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity')) {
      try {
        if (obj instanceof Textbox || obj instanceof TextEntity) {
          // When user modifies font style, we assume they want to keep this version of text
          // and don't need to restore the original text anymore
          obj.set('is_truncated', false);

          if (obj.selectionStart === obj.selectionEnd) {
            // No selection -> apply style to the whole text
            switch (style) {
              case 'regular':
                obj.set({
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                  underline: false,
                });
                //processing obj.styles (standard Fabric.js format)
                Object.keys(obj.styles).forEach((key) => {
                  const s = obj.styles[key];
                  Object.values(s).forEach((style: any) => {
                    if (style.fontWeight) delete style.fontWeight;
                    if (style.underline) delete style.underline;
                  });
                });

                //processing array styles with fields start, end, style
                if (Array.isArray(obj.styles)) {
                  obj.styles.forEach((styleItem: any) => {
                    if (styleItem.style) {
                      if (styleItem.style.fontWeight) delete styleItem.style.fontWeight;
                      if (styleItem.style.underline) delete styleItem.style.underline;
                    }
                  });
                }
                obj.dirty = true;
                break;
              case 'bold':
                obj.set({
                  fontWeight: 'bold',
                  fontStyle: 'normal',
                  underline: false,
                });
                //processing obj.styles (standard Fabric.js format)
                Object.keys(obj.styles).forEach((key) => {
                  const s = obj.styles[key];
                  Object.values(s).forEach((style: any) => {
                    style.fontWeight = 'bold';
                    //save other styles if they exist
                  });
                });

                //processing array styles with fields start, end, style
                if (Array.isArray(obj.styles)) {
                  obj.styles.forEach((styleItem: any) => {
                    if (styleItem.style) {
                      styleItem.style.fontWeight = 'bold';
                    }
                  });
                }
                obj.dirty = true;
                break;
              case 'underline':
                obj.set({
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                  underline: true,
                });
                //processing obj.styles (standard Fabric.js format)
                Object.keys(obj.styles).forEach((key) => {
                  const s = obj.styles[key];
                  Object.values(s).forEach((style: any) => {
                    style.underline = true;
                    //save other styles if they exist
                  });
                });

                //processing array styles with fields start, end, style
                if (Array.isArray(obj.styles)) {
                  obj.styles.forEach((styleItem: any) => {
                    if (styleItem.style) {
                      styleItem.style.underline = true;
                    }
                  });
                }
                obj.dirty = true;
                break;
            }
          } else {
            // There is selection -> apply style only to the selected part
            const selectionStyles: any = {};
            switch (style) {
              case 'regular':
                selectionStyles.fontWeight = 'normal';
                selectionStyles.fontStyle = 'normal';
                selectionStyles.underline = false;
                break;
              case 'bold':
                selectionStyles.fontWeight = 'bold';
                break;
              case 'underline':
                selectionStyles.underline = true;
                break;
            }
            obj.setSelectionStyles(selectionStyles, obj.selectionStart, obj.selectionEnd);

            //processing array styles with fields start, end, style for selected text
            if (Array.isArray(obj.styles)) {
              const start = obj.selectionStart;
              const end = obj.selectionEnd;

              //check if there is already a style for this range
              const existingStyleIndex = obj.styles.findIndex(
                (styleItem: any) => styleItem.start === start && styleItem.end === end,
              );

              if (existingStyleIndex !== -1) {
                //update existing style
                switch (style) {
                  case 'regular':
                    if (obj.styles[existingStyleIndex].style.fontWeight)
                      delete obj.styles[existingStyleIndex].style.fontWeight;
                    if (obj.styles[existingStyleIndex].style.underline)
                      delete obj.styles[existingStyleIndex].style.underline;
                    break;
                  case 'bold':
                    obj.styles[existingStyleIndex].style.fontWeight = 'bold';
                    break;
                  case 'underline':
                    obj.styles[existingStyleIndex].style.underline = true;
                    break;
                }
              } else {
                //create new style for selected range
                const newStyle: any = { start, end, style: {} };
                switch (style) {
                  case 'regular':
                    newStyle.style.fontWeight = 'normal';
                    newStyle.style.underline = false;
                    break;
                  case 'bold':
                    newStyle.style.fontWeight = 'bold';
                    break;
                  case 'underline':
                    newStyle.style.underline = true;
                    break;
                }
                obj.styles.push(newStyle);
              }
            }

            obj.dirty = true;
          }
        }
      } catch (e) {
        console.error('⛔ Error', e);
      }
    }
  });
  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Удаляет определенный стиль форматирования текста, сохраняя другие стили
 * @param params - Параметры для удаления стиля
 */
export const removeFontStyle = ({
  style,
  canvas,
  targetIds,
  canvasId,
  addBackup,
}: MenuItemWithBackup & { style: 'bold' | 'underline' }) => {
  const canvasObjects = canvas.getObjects();

  targetIds.forEach((id) => {
    const obj = canvasObjects.find((o: FabricObject) => {
      if (o.id === id) {
        return true;
      } else if (o.category) {
        // Если объект в группе (bullets)
        if (isBullet(o) && o instanceof Group) {
          performActionOnObjectInGroup(o, id, (obj) => {
            if (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity') {
              const textObject = obj as Textbox | TextEntity;

              switch (style) {
                case 'bold':
                  // Убираем жирность, сохраняя другие стили
                  textObject.set({
                    fontWeight: 'normal',
                  });

                  // Обрабатываем textObject.styles (стандартный формат Fabric.js)
                  Object.keys(textObject.styles).forEach((key) => {
                    const s = textObject.styles[key];
                    Object.values(s).forEach((styleObj: any) => {
                      if (styleObj.fontWeight) delete styleObj.fontWeight;
                    });
                  });

                  // Обрабатываем массив стилей с полями start, end, style
                  if (Array.isArray(textObject.styles)) {
                    textObject.styles.forEach((styleItem: any) => {
                      if (styleItem.style && styleItem.style.fontWeight) {
                        delete styleItem.style.fontWeight;
                      }
                    });
                  }
                  textObject.dirty = true;
                  textObject._clearCache();
                  textObject.setCoords();
                  break;

                case 'underline':
                  // Убираем подчеркивание, сохраняя другие стили
                  textObject.set({
                    underline: false,
                  });

                  // Обрабатываем textObject.styles (стандартный формат Fabric.js)
                  Object.keys(textObject.styles).forEach((key) => {
                    const s = textObject.styles[key];
                    Object.values(s).forEach((styleObj: any) => {
                      if (styleObj.underline) delete styleObj.underline;
                    });
                  });

                  // Обрабатываем массив стилей с полями start, end, style
                  if (Array.isArray(textObject.styles)) {
                    textObject.styles.forEach((styleItem: any) => {
                      if (styleItem.style && styleItem.style.underline) {
                        delete styleItem.style.underline;
                      }
                    });
                  }
                  textObject.dirty = true;
                  textObject._clearCache();
                  textObject.setCoords();
                  break;
              }
              canvas.discardActiveObject();
              canvas.setActiveObject(textObject);
            }
          });

          return;
        }
      }
      return false;
    });

    if (obj && (obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity')) {
      try {
        if (obj instanceof Textbox || obj instanceof TextEntity) {
          if (obj.selectionStart === obj.selectionEnd) {
            // Нет выделения -> применяем стиль ко всему тексту
            switch (style) {
              case 'bold':
                // Убираем жирность, сохраняя другие стили
                obj.set({
                  fontWeight: 'normal',
                });

                // Обрабатываем obj.styles (стандартный формат Fabric.js)
                Object.keys(obj.styles).forEach((key) => {
                  const s = obj.styles[key];
                  Object.values(s).forEach((styleObj: any) => {
                    if (styleObj.fontWeight) delete styleObj.fontWeight;
                  });
                });

                // Обрабатываем массив стилей с полями start, end, style
                if (Array.isArray(obj.styles)) {
                  obj.styles.forEach((styleItem: any) => {
                    if (styleItem.style && styleItem.style.fontWeight) {
                      delete styleItem.style.fontWeight;
                    }
                  });
                }
                obj.dirty = true;
                break;

              case 'underline':
                // Убираем подчеркивание, сохраняя другие стили
                obj.set({
                  underline: false,
                });

                // Обрабатываем obj.styles (стандартный формат Fabric.js)
                Object.keys(obj.styles).forEach((key) => {
                  const s = obj.styles[key];
                  Object.values(s).forEach((styleObj: any) => {
                    if (styleObj.underline) delete styleObj.underline;
                  });
                });

                // Обрабатываем массив стилей с полями start, end, style
                if (Array.isArray(obj.styles)) {
                  obj.styles.forEach((styleItem: any) => {
                    if (styleItem.style && styleItem.style.underline) {
                      delete styleItem.style.underline;
                    }
                  });
                }
                obj.dirty = true;
                break;
            }
          } else {
            // Есть выделение -> применяем стиль только к выделенной части
            const selectionStyles: any = {};
            switch (style) {
              case 'bold':
                selectionStyles.fontWeight = 'normal';
                break;
              case 'underline':
                selectionStyles.underline = false;
                break;
            }
            obj.setSelectionStyles(selectionStyles, obj.selectionStart, obj.selectionEnd);

            // Обрабатываем массив стилей с полями start, end, style для выделенного текста
            if (Array.isArray(obj.styles)) {
              const start = obj.selectionStart;
              const end = obj.selectionEnd;

              // Проверяем, есть ли уже стиль для этого диапазона
              const existingStyleIndex = obj.styles.findIndex(
                (styleItem: any) => styleItem.start === start && styleItem.end === end,
              );

              if (existingStyleIndex !== -1) {
                // Обновляем существующий стиль
                switch (style) {
                  case 'bold':
                    if (obj.styles[existingStyleIndex].style.fontWeight) {
                      delete obj.styles[existingStyleIndex].style.fontWeight;
                    }
                    break;
                  case 'underline':
                    if (obj.styles[existingStyleIndex].style.underline) {
                      delete obj.styles[existingStyleIndex].style.underline;
                    }
                    break;
                }
              } else {
                // Создаем новый стиль для выделенного диапазона
                const newStyle: any = { start, end, style: {} };
                switch (style) {
                  case 'bold':
                    newStyle.style.fontWeight = 'normal';
                    break;
                  case 'underline':
                    newStyle.style.underline = false;
                    break;
                }
                obj.styles.push(newStyle);
              }
            }

            obj.dirty = true;
          }
        }
      } catch (e) {
        console.error('⛔ Error removing font style', e);
      }
    }
  });

  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Определяет текущий размер шрифта с учетом группового выделения
 * @param targetIds - Массив ID выделенных объектов
 * @param canvas - Canvas объект
 * @returns Размер шрифта (единый или средний при разных размерах)
 */
export const getCurrentFontSize = (targetIds: string[], canvas: any): number => {
  if (!targetIds || targetIds.length === 0) return 12;

  const objects = canvas.getObjects();
  const fontSizes: number[] = [];

  targetIds.forEach((id) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let targetObject = objects.find((obj: FabricObject) => (obj as any).id === id);
    let fontSize: number | undefined;

    if (targetObject) {
      // Объект найден на верхнем уровне
      if (
        targetObject.type?.toLowerCase() === 'textbox' ||
        targetObject.type?.toLowerCase() === 'textentity'
      ) {
        // Обычный текстовый объект
        fontSize = (targetObject.get('fontSize') as number) || 12;
      } else if (isBullet(targetObject) && targetObject instanceof Group) {
        // Буллит - ищем текстовую часть внутри группы
        const textObjects = targetObject
          .getObjects()
          .filter(
            (obj) =>
              obj.type?.toLowerCase() === 'textbox' || obj.type?.toLowerCase() === 'textentity',
          );

        if (textObjects.length > 0) {
          // Берем размер шрифта первого текстового объекта в буллите
          fontSize = (textObjects[0].get('fontSize') as number) || 12;
        }
      }
    } else {
      // Объект не найден на верхнем уровне - ищем внутри групп буллитов
      for (const obj of objects) {
        if (isBullet(obj) && obj instanceof Group) {
          const foundInGroup = obj.getObjects().find((innerObj: any) => innerObj.id === id);
          if (
            foundInGroup &&
            (foundInGroup.type?.toLowerCase() === 'textbox' ||
              foundInGroup.type?.toLowerCase() === 'textentity')
          ) {
            targetObject = foundInGroup; // Переназначаем targetObject
            fontSize = (targetObject.get('fontSize') as number) || 12;
            break; // Нашли объект, выходим из цикла
          }
        }
      }
    }

    if (fontSize !== undefined) {
      fontSizes.push(fontSize);
    }
  });

  if (fontSizes.length === 0) return 12;

  const uniqueFontSizes = [...new Set(fontSizes)];
  if (uniqueFontSizes.length === 1) {
    // Все объекты имеют одинаковый размер шрифта
    return uniqueFontSizes[0];
  } else {
    // Разные размеры шрифта - используем средний размер как базовый
    return Math.round(fontSizes.reduce((a, b) => a + b, 0) / fontSizes.length);
  }
};

/**
 * Устанавливает конкретный размер шрифта для выделенных текстовых объектов
 * @param params - Параметры для установки размера шрифта
 * @param newSize - Новый размер шрифта (от 1 до 100)
 */
export const setFontSize = (
  { targetIds, canvas, canvasId, addBackup, item }: MenuItemWithBackup,
  newSize: number,
) => {
  // Для каждого выделенного объекта вычисляем adjustment и применяем adjustFontSize
  const objects = canvas.getObjects();
  targetIds.forEach((id) => {
    const targetObject = objects.find((obj: any) => {
      if (obj.id === id) {
        return true;
      } else if (obj.category) {
        // Проверяем, если объект в группе (bullets)
        if (isBullet(obj) && obj instanceof Group) {
          // Для bullet групп также вычисляем adjustment и применяем adjustFontSize
          const currentSize = getCurrentFontSize([id], canvas);
          const adjustment = Math.max(1, Math.min(100, newSize)) - currentSize;
          adjustFontSize({ item, targetIds: [id], canvas, canvasId, addBackup }, adjustment, true);
          return true; // объект найден и обработан
        }
      }
      return false;
    });

    if (
      targetObject &&
      (targetObject.type?.toLowerCase() === 'textbox' ||
        targetObject.type?.toLowerCase() === 'textentity')
    ) {
      const currentSize = (targetObject.get('fontSize') as number) || 12;
      const adjustment = Math.max(1, Math.min(100, newSize)) - currentSize;

      // Применяем adjustFontSize только к этому объекту
      adjustFontSize({ item, targetIds: [id], canvas, canvasId, addBackup }, adjustment, true);
    }
  });
};

/**
 * Определяет текущее выравнивание текста с учетом группового выделения
 * @param targetIds - Массив ID выделенных объектов
 * @param canvas - Canvas объект
 * @returns Выравнивание текста (единое или undefined при разных выравниваниях)
 */
export const getCurrentTextAlignment = (targetIds: string[], canvas: any): string | undefined => {
  if (!targetIds || targetIds.length === 0) return undefined;

  const objects = canvas.getObjects();
  const alignments: string[] = [];

  targetIds.forEach((id) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let targetObject = objects.find((obj: FabricObject) => (obj as any).id === id);
    let alignment: string | undefined;

    if (targetObject) {
      // Объект найден на верхнем уровне
      if (
        targetObject.type?.toLowerCase() === 'textbox' ||
        targetObject.type?.toLowerCase() === 'textentity'
      ) {
        // Обычный текстовый объект
        alignment = (targetObject.get('textAlign') as string) || 'left';
      } else if (isBullet(targetObject) && targetObject instanceof Group) {
        // Буллит - ищем текстовую часть внутри группы
        const textObjects = targetObject
          .getObjects()
          .filter(
            (obj) =>
              obj.type?.toLowerCase() === 'textbox' || obj.type?.toLowerCase() === 'textentity',
          );

        if (textObjects.length > 0) {
          // Берем выравнивание первого текстового объекта в буллите
          alignment = (textObjects[0].get('textAlign') as string) || 'left';
        }
      }
    } else {
      // Объект не найден на верхнем уровне - ищем внутри групп буллитов
      for (const obj of objects) {
        if (isBullet(obj) && obj instanceof Group) {
          const foundInGroup = obj.getObjects().find((innerObj: any) => innerObj.id === id);
          if (
            foundInGroup &&
            (foundInGroup.type?.toLowerCase() === 'textbox' ||
              foundInGroup.type?.toLowerCase() === 'textentity')
          ) {
            targetObject = foundInGroup; // Переназначаем targetObject
            alignment = (targetObject.get('textAlign') as string) || 'left';
            break; // Нашли объект, выходим из цикла
          }
        }
      }
    }

    if (alignment !== undefined) {
      alignments.push(alignment);
    }
  });

  if (alignments.length === 0) return undefined;

  const uniqueAlignments = [...new Set(alignments)];
  if (uniqueAlignments.length === 1) {
    // Все объекты имеют одинаковое выравнивание
    return uniqueAlignments[0];
  } else {
    // Разные выравнивания - возвращаем undefined
    return undefined;
  }
};
